import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { api } from '../../utils/apiClient';
import StatusBadge from '../../components/quote/StatusBadge';
import { formatMoney } from '../../components/quote/TotalsPanel';
import { ROUTE } from '../../utils/navigation';

const STATUS_OPTIONS = ['', 'Draft', 'Sent', 'Opened', 'Completed', 'Archived', 'Cancelled'];

const QuotesListPage = ({ onNavigate, storeCurrency = 'USD' }) => {
  const { t, i18n } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const currency = storeCurrency || 'USD';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = {};
      if (status) query.status = status;
      if (search.trim()) query.search = search.trim();
      const data = await api.listQuotes(query);
      setQuotes(data?.quotes || []);
    } catch {
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="py-3 px-2">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h2 className="h4 mb-1">{t('quotesList.title')}</h2>
          <p className="text-muted mb-0">{t('quotesList.subtitle')}</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            className="d-flex align-items-center gap-1"
            onClick={load}
            disabled={loading}
            title={t('common.refresh', { defaultValue: 'Refresh' })}
          >
            <RefreshCw size={16} className={loading ? 'spin' : undefined} />
            {t('common.refresh', { defaultValue: 'Refresh' })}
          </Button>
          <Button variant="primary" className="d-flex align-items-center gap-1" onClick={() => onNavigate(ROUTE.QUOTES_CREATE)}>
            <Plus size={16} />
            {t('quotesList.createQuote')}
          </Button>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <div className="position-relative">
            <Form.Control
              placeholder={t('quotesList.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
            />
            <Search size={16} className="position-absolute text-muted" style={{ right: 12, top: 10 }} />
          </div>
        </div>
        <div className="col-md-3">
          <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s || 'all'} value={s}>
                {s ? t(`status.${s}`, { defaultValue: s }) : t('quotesList.allStatuses')}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-3">
          <Button variant="outline-secondary" className="w-100" onClick={load}>
            {t('quotesList.applyFilters')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : quotes.length === 0 ? (
        <div className="text-center text-muted py-5 border rounded bg-white">{t('quotesList.noQuotes')}</div>
      ) : (
        <div className="table-responsive bg-white rounded border shadow-sm">
          <Table hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>{t('quotesList.colQuoteNumber')}</th>
                <th>{t('quotesList.colCustomer')}</th>
                <th>{t('quotesList.colEmail')}</th>
                <th>{t('quotesList.colDate')}</th>
                <th>{t('quotesList.colTotal')}</th>
                <th>{t('quotesList.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr
                  key={q.id}
                  role="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onNavigate(`quotes/${q.id}`)}
                >
                  <td className="fw-semibold">{q.number}</td>
                  <td>{q.customer?.name}</td>
                  <td className="text-muted small">{q.customer?.email}</td>
                  <td className="text-muted small">{q.createdAt ? new Date(q.createdAt).toLocaleDateString(i18n.language) : '—'}</td>
                  <td>{formatMoney(q.total, q.currency || currency)}</td>
                  <td>
                    <StatusBadge status={q.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default QuotesListPage;
